import React, { useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  useTheme,
  Tooltip,
  Skeleton,
  Alert,
  FormControlLabel,
  Switch,
  Stack,
} from '@mui/material';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import LockIconWrapper from '../../../components/LockIconWrapper';
import DatePicker from '../../../Bookings/CreateBookingsModal/components/components/DatePicker';
import dayjs from 'dayjs';

function ShiftBookingDialog({
  open,
  onClose,
  booking,
  bookingStartTime,
  currentMonth,
  currentYear,
  availableDates,
  availableTimes,
  selectedDate,
  selectedTime,
  loadingDates,
  loadingTimes,
  onNavigate,
  onDateSelect,
  onTimeSelect,
  onShiftConfirm,
  canOverbook = false,
  overbookEnabled = false,
  onOverbookToggle,
}) {
  const theme = useTheme();

  useEffect(() => {
    if (selectedDate) {
      const selectedDay = dayjs(selectedDate);
      if (selectedDay.month() !== currentMonth - 1 || selectedDay.year() !== currentYear) {
        onDateSelect(null);
      }
    }
  }, [currentMonth, currentYear, selectedDate, onDateSelect]);

  const originalBookingDate = useMemo(
    () => (bookingStartTime ? dayjs(bookingStartTime) : null),
    [bookingStartTime]
  );

  const datePickerData = useMemo(() => {
    if (!availableDates) return [];
    return availableDates.map(dateString => {
      const date = dayjs(dateString);
      const isOriginal = originalBookingDate && date.isSame(originalBookingDate, 'day');
      return {
        day: date.date(),
        isBookable: true,
        isOriginal,
      };
    });
  }, [availableDates, originalBookingDate]);

  const handleDateSelect = date => {
    if (date && dayjs(date).isValid() && dayjs(date).month() === currentMonth - 1) {
      onDateSelect(date);
    }
  };

  const originalBookingTime = useMemo(
    () => (originalBookingDate ? originalBookingDate.format('HH:mm') : null),
    [originalBookingDate]
  );

  const normalizedAvailableTimes = useMemo(() => {
    if (!Array.isArray(availableTimes)) return [];
    return availableTimes.map(t => {
      if (t && typeof t === 'object') {
        return {
          startTime: t.startTime,
          overbookedBy: Number(t.overbookedBy) || 0,
        };
      }
      return { startTime: t, overbookedBy: 0 };
    });
  }, [availableTimes]);

  const shiftInfo = useMemo(() => {
    const history = Array.isArray(booking?.history) ? booking.history : [];
    const shiftEntries = history.filter(entry =>
      entry.keys?.some(k => k.key === 'startTime' || k.key === 'endTime')
    );
    if (shiftEntries.length === 0) return null;
    const lastShift = shiftEntries[shiftEntries.length - 1];
    const lastShiftAt = lastShift?.changedAt;
    return {
      count: shiftEntries.length,
      lastShiftAt,
    };
  }, [booking?.history]);

  const isOriginalDateSelected = useMemo(
    () =>
      originalBookingDate && selectedDate && dayjs(selectedDate).isSame(originalBookingDate, 'day'),
    [originalBookingDate, selectedDate]
  );

  const handleNavigate = dateChange => {
    const { month, year } = dateChange;
    const targetDate = dayjs(new Date(year, month - 1, 1));
    const currentDate = dayjs().startOf('month');

    if (targetDate.isBefore(currentDate)) {
      return;
    }

    onNavigate(dateChange);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>Buchung verschieben</DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        {shiftInfo && (
          <Alert
            severity='warning'
            sx={{
              mb: 2,
              bgcolor: '#fff3e0',
              borderLeft: '4px solid',
              borderColor: 'warning.main',
              color: 'grey.800',
            }}
          >
            <Typography variant='body2'>
              Diese Buchung wurde bereits {shiftInfo.count} Mal verschoben
              {shiftInfo.lastShiftAt &&
                ` - zuletzt am ${dayjs(shiftInfo.lastShiftAt).format('DD.MM.YYYY HH:mm')}.`}
            </Typography>
          </Alert>
        )}
        {canOverbook && (
          <Stack direction='row' justifyContent='flex-end' sx={{ mb: 2 }}>
            <Tooltip title='Ermöglicht das Verschieben auf Termine, bei denen die Kapazität nicht ausreicht.'>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!overbookEnabled}
                    onChange={e => onOverbookToggle?.(e.target.checked)}
                    color='warning'
                  />
                }
                label={
                  <Typography variant='body2' sx={{ color: 'warning.main' }}>
                    Überbuchung erlauben
                  </Typography>
                }
              />
            </Tooltip>
          </Stack>
        )}
        {overbookEnabled && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            <Typography variant='body2'>
              Überbuchung ist aktiv. Gelb markierte Zeiten überschreiten die normale Kapazität.
            </Typography>
          </Alert>
        )}
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <DatePicker
              month={currentMonth}
              year={currentYear}
              onNavigate={handleNavigate}
              data={datePickerData}
              selectedDate={selectedDate}
              onChange={handleDateSelect}
              disabled={loadingDates}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <Typography variant='h6' gutterBottom sx={{ mb: 2 }}>
              Uhrzeit auswählen
            </Typography>
            {loadingDates ? (
              <Box
                display='flex'
                justifyContent='center'
                alignItems='center'
                sx={{ height: '100%' }}
              >
                <Typography variant='body2' color='text.secondary'>
                  Monat wird geladen...
                </Typography>
              </Box>
            ) : loadingTimes ? (
              <Box
                sx={{
                  height: {
                    md: 'calc(100% - 40px)',
                  },
                  overflowY: 'auto',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Grid container spacing={1} columns={{ xs: 2, sm: 8, md: 4 }}>
                  {Array.from(new Array(12)).map((_, index) => (
                    <Grid item xs={1} key={index}>
                      <Skeleton variant='rectangular' height={28} sx={{ borderRadius: '20px' }} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ) : (
              <Box
                sx={{
                  height: {
                    md: 'calc(100% - 40px)',
                  },
                  overflowY: 'auto',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  p: 2,
                }}
              >
                {selectedDate ? (
                  <Grid container spacing={1} columns={{ xs: 2, sm: 8, md: 4 }}>
                    {normalizedAvailableTimes.length > 0 ? (
                      normalizedAvailableTimes.map(({ startTime: time, overbookedBy }) => {
                        const isOriginalTime =
                          isOriginalDateSelected && time === originalBookingTime;
                        const isOverbook = overbookedBy > 0;
                        return (
                          <Grid item xs={1} key={time}>
                            {isOriginalTime ? (
                              <Tooltip title='Bereits gebuchte Uhrzeit' placement='top'>
                                <span style={{ display: 'inline-block', width: '100%' }}>
                                  <Button
                                    variant='outlined'
                                    disabled
                                    disableRipple
                                    sx={{
                                      cursor: 'default',
                                      opacity: 0.5,
                                      borderWidth: '2px',
                                      borderColor: 'primary.main',
                                      borderRadius: '20px',
                                      width: '100%',
                                      py: 0.5,
                                      '&:hover': {
                                        backgroundColor: 'transparent',
                                      },
                                    }}
                                  >
                                    <Typography variant='body2' sx={{ lineHeight: 1 }}>
                                      {time}
                                    </Typography>
                                  </Button>
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip
                                title={
                                  isOverbook
                                    ? `Überbuchung: ${overbookedBy} Platz/Plätze mehr als verfügbar`
                                    : ''
                                }
                                placement='top'
                              >
                                <Button
                                  variant={selectedTime === time ? 'contained' : 'outlined'}
                                  onClick={() => onTimeSelect(time)}
                                  sx={{
                                    color: selectedTime === time ? '#3b3a39' : 'text.primary',
                                    borderColor: isOverbook
                                      ? 'warning.main'
                                      : isOriginalTime
                                        ? 'primary.main'
                                        : '#afaeae',
                                    borderWidth: isOriginalTime || isOverbook ? '2px' : '1px',
                                    backgroundColor:
                                      selectedTime === time
                                        ? isOverbook
                                          ? 'warning.light'
                                          : 'grey.200'
                                        : isOverbook
                                          ? 'rgba(255, 193, 7, 0.08)'
                                          : 'transparent',
                                    borderRadius: '20px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 0.25,
                                    py: 0.5,
                                    minWidth: { xs: '50px', sm: '60px' },
                                    width: '100%',
                                    '&:hover': {
                                      backgroundColor: isOverbook ? 'warning.light' : 'grey.400',
                                      borderColor: isOverbook ? 'warning.dark' : '#afaeae',
                                    },
                                  }}
                                >
                                  <Typography variant='body2' sx={{ lineHeight: 1 }}>
                                    {time}
                                  </Typography>
                                  {isOverbook && (
                                    <Box
                                      sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}
                                    >
                                      <GroupAddIcon
                                        sx={{ fontSize: '0.8rem', color: 'warning.dark' }}
                                      />
                                      <Typography
                                        variant='caption'
                                        sx={{
                                          fontSize: '0.6rem',
                                          lineHeight: 1,
                                          color: 'warning.dark',
                                          fontWeight: 'bold',
                                        }}
                                      >
                                        +{overbookedBy}
                                      </Typography>
                                    </Box>
                                  )}
                                </Button>
                              </Tooltip>
                            )}
                          </Grid>
                        );
                      })
                    ) : (
                      <Typography variant='body2' color='text.secondary'>
                        Keine verfügbaren Zeiten für dieses Datum.
                      </Typography>
                    )}
                  </Grid>
                ) : (
                  <Box
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                    sx={{ height: '100%' }}
                  >
                    <Typography variant='body2' color='text.secondary'>
                      Bitte ein Datum auswählen.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        <Button
          onClick={onClose}
          variant='outlined'
          sx={{
            color: 'error.main',
            borderColor: 'error.main',
            '&:hover': {
              backgroundColor: 'error.main',
              color: 'error.contrastText',
              borderColor: 'error.main',
            },
          }}
        >
          Abbrechen
        </Button>
        <Button
          onClick={onShiftConfirm}
          variant='contained'
          disabled={!selectedDate || !selectedTime || loadingTimes}
        >
          <LockIconWrapper right='internalBooking:update:own'>Bestätigen</LockIconWrapper>
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ShiftBookingDialog;
